import React from "react";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";

const STATUS_CHIP = {
  operational: "green",
  maintenance: "amber",
  down: "red",
  coming_soon: "gold",
  degraded: "amber",
};

export default function StatusPage() {
  const { t } = useLang();
  const [data, setData] = React.useState(null);
  React.useEffect(() => { api.get("/api/status").then((r) => setData(r.data)); }, []);
  if (!data) {
    return (
      <div>
        <Header />
      <Seo title={t("seo.status.title")} description={t("seo.status.desc")} path="/status" />
        <div className="max-w-5xl mx-auto px-6 py-16"><div className="h-40 lp-shimmer rounded-2xl" /></div>
      </div>
    );
  }
  const overall = data.overall;
  return (
    <div>
      <Header />
      <section className="max-w-5xl mx-auto px-6 py-14">
        <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("status.eyebrow")}</div>
        <h1 className="font-display text-4xl text-white/95">{t("status.title")}</h1>
        <div className={`mt-4 flex items-center gap-3 ${overall === "operational" ? "text-emerald-400" : "text-amber-400"}`}>
          {overall === "operational" ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="text-lg">{overall === "operational" ? t("status.all_operational") : t("status.some_degraded")}</span>
        </div>

        <div className="lp-card p-6 mt-8" data-testid="status-services">
          <h3 className="font-display text-white/90 mb-4">{t("status.services")}</h3>
          <div className="divide-y divide-white/8">
            {data.services.map((s) => (
              <div key={s.slug} className="py-3 flex items-center justify-between" data-testid={`status-svc-${s.slug}`}>
                <div className="text-white/85">{displayProductName(s.name)}</div>
                <span className={`lp-chip ${STATUS_CHIP[s.status] || "amber"}`}>
                  {s.status === "coming_soon" ? (
                    <>
                      <Clock className="w-3 h-3" /> {t("marketplace.coming_soon")}
                    </>
                  ) : s.status === "operational" ? t("status.chip.operational")
                    : s.status === "maintenance" ? t("status.chip.maintenance")
                    : s.status === "down" ? t("status.chip.down")
                    : s.status === "degraded" ? t("status.chip.degraded")
                    : s.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="lp-card p-6 mt-6" data-testid="status-history">
          <h3 className="font-display text-white/90 mb-4">{t("status.uptime_60d")}</h3>
          <div className="flex gap-[3px] flex-wrap">
            {data.history.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.status}`}
                className={`w-[8px] h-8 rounded-sm ${d.status === "operational" ? "bg-emerald-500/60" : "bg-amber-400/70"}`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-white/40 mt-2 font-mono">
            <span>{t("status.60d_ago")}</span>
            <span>{t("status.today")}</span>
          </div>
        </div>

        <div className="lp-card p-6 mt-6" data-testid="status-incidents">
          <h3 className="font-display text-white/90 mb-4">{t("status.recent_incidents")}</h3>
          {data.incidents.length === 0 && <div className="text-white/50">{t("status.no_incidents")}</div>}
          <div className="space-y-4">
            {data.incidents.map((i) => (
              <div key={i.id} className="p-4 rounded-xl border border-white/8">
                <div className="flex items-center gap-2">
                  <span className="text-white/90 font-medium">{i.title}</span>
                  <span className={`lp-chip ${i.status === "resolved" ? "green" : "amber"}`}>{i.status}</span>
                  <span className="lp-chip">{i.severity}</span>
                </div>
                <p className="text-white/60 text-sm mt-2">{i.body}</p>
                <div className="text-xs text-white/40 mt-2 font-mono">{new Date(i.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

